import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import store, { showToast, showToastWithType } from '../core/store';
import { getFormDefinitionById, listFormDefinitions, updateFormDefinition } from '../../util/APIUtils';
import { GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS } from './constants/form-api.constants';
import { isPermitted } from '../../components/permission-control/permission-control';
import { FORM_CREATE, FORM_MANAGE, FORM_VIEW } from './constants/form.permission';
import {
  FORMS_COLUMNS,
  FORMS_PAGE_SIZE,
  buildDuplicateFormCreatePayload,
  buildFormsCsvExportPayload,
  mapApiRowsToForms,
  mapFormsToGridData,
} from './utils/form.util';
import { downloadCsv } from './utils/csv-export.util';
import {
  FORMS_DUPLICATE_ERROR,
  FORMS_DUPLICATE_SUCCESS,
  FORMS_LOAD_ERROR,
  FORMS_ROUTE,
  FORMS_UPDATE_ERROR,
  formEditRoute,
  formSubmissionsRoute,
} from './constants/forms.constants';
import { createNewForm, getErrorToastMessage } from './utils/form-manage.util';
import { buildFormSourceRoute } from './utils/form-source.util';
import { DEFAULT_FORM_SOURCE } from './constants/form-source.constants';

/**
 * State and row actions for the shared Forms list.
 * @param {{ source?: string }} [options] - Host module (see `FORM_SOURCE`); carried into
 *   submissions / create / edit URLs so their back navigation returns to the same Forms tab.
 */
export const useFormsHook = ({ source = DEFAULT_FORM_SOURCE } = {}) => {
  const navigate = useNavigate();
  const [global] = useGlobal();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewForm, setPreviewForm] = useState(null);

  const canViewForm = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FORM_VIEW, permissionIds);
  }, [global]);

  const canCreateForm = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FORM_CREATE, permissionIds);
  }, [global]);

  const canManageForm = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FORM_MANAGE, permissionIds);
  }, [global]);

  const fetchFormsSilently = useCallback(async () => {
    const rows = await listFormDefinitions();
    setForms(mapApiRowsToForms(rows));
  }, []);

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      await fetchFormsSilently();
    } catch {
      setForms([]);
      store.dispatch(showToast(FORMS_LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  }, [fetchFormsSilently]);

  const handleToggleActive = useCallback(
    async (form) => {
      setLoading(true);
      try {
        await updateFormDefinition(form.id, { is_active: !form.active });
        await fetchFormsSilently();
      } catch {
        store.dispatch(showToast(FORMS_UPDATE_ERROR));
      } finally {
        setLoading(false);
      }
    },
    [fetchFormsSilently],
  );

  const handleViewSubmissions = useCallback(
    (form) => {
      if (form?.id == null || form.id === '') {
        return;
      }
      navigate(buildFormSourceRoute(formSubmissionsRoute(form.id), source));
    },
    [navigate, source],
  );

  const handleEditForm = useCallback(
    (form) => {
      navigate(buildFormSourceRoute(formEditRoute(form.id), source));
    },
    [navigate, source],
  );

  const handleDuplicateForm = useCallback(
    async (form) => {
      if (form?.id == null || form.id === '') {
        return;
      }
      setLoading(true);
      try {
        const definition = await getFormDefinitionById(String(form.id), GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS);
        const payload = buildDuplicateFormCreatePayload(definition);
        await createNewForm(payload);
        store.dispatch(showToastWithType(FORMS_DUPLICATE_SUCCESS, 'success'));
        await fetchFormsSilently();
      } catch (err) {
        const message =
          err?.message === 'INACTIVE_FORM' ? FORMS_DUPLICATE_ERROR : await getErrorToastMessage(err);
        store.dispatch(showToast(message));
      } finally {
        setLoading(false);
      }
    },
    [fetchFormsSilently],
  );

  const onPreviewForm = useCallback((form) => {
    setPreviewForm(form);
  }, []);

  const clearPreviewForm = useCallback(() => {
    setPreviewForm(null);
  }, []);

  const handleCreateNewForm = useCallback(() => {
    navigate(buildFormSourceRoute(FORMS_ROUTE, source));
  }, [navigate, source]);

  const actionHandlers = useMemo(
    () => ({
      ...(canViewForm
        ? { onPreviewForm, onViewSubmissions: handleViewSubmissions }
        : {}),
      ...(canCreateForm ? { onDuplicateForm: handleDuplicateForm } : {}),
      ...(canManageForm
        ? { onEditForm: handleEditForm, onToggleActive: handleToggleActive }
        : {}),
    }),
    [
      canViewForm,
      canCreateForm,
      canManageForm,
      handleToggleActive,
      onPreviewForm,
      handleViewSubmissions,
      handleEditForm,
      handleDuplicateForm,
    ],
  );

  const data = useMemo(
    () => mapFormsToGridData(forms, actionHandlers),
    [forms, actionHandlers],
  );

  const primaryButtonProps = useMemo(
    () => {
      if (!canCreateForm) {
        return undefined;
      }
      return {
        text: 'Create New Form',
        variant: 'contained',
        startIcon: <AddIcon />,
        onClick: handleCreateNewForm,
      };
    },
    [handleCreateNewForm, canCreateForm],
  );

  const handleExportForms = useCallback(() => {
    downloadCsv(buildFormsCsvExportPayload(forms));
  }, [forms]);

  const secondaryButtonProps = useMemo(
    () => ({
      text: 'Export CSV',
      variant: 'outlined',
      startIcon: <SaveAltIcon />,
      onClick: handleExportForms,
      disabled: loading || forms.length === 0,
    }),
    [handleExportForms, loading, forms.length],
  );

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  return {
    columns: FORMS_COLUMNS,
    data,
    pageSize: FORMS_PAGE_SIZE,
    loading,
    refreshForms: loadForms,
    primaryButtonProps,
    secondaryButtonProps,
    previewForm,
    clearPreviewForm,
  };
};
